import React, { useEffect, useState } from 'react';
import { Registerer, UserAgent } from 'sip.js';
import { useDispatch } from 'react-redux';
import { Button } from 'reactstrap';

import AdminTimer from '../../utils/AdminTimer';
import config from '../../config';

import { useUserDetails } from '../../store/slices/authSlice';
import { hangupCall } from '../../store/slices/callingSlice';
import {
  setActiveCallName,
  setCallId,
  setSipStatus,
  useCallId,
  usesActiveCallName
} from '../../store/slices/dialSlice';
import {
  setCallModalOpen,
  useIsCallModalOpen
} from '../../store/slices/tenantSlices/supervisorSlice';

const CallingModal = () => {
  const dispatch = useDispatch();
  const activeCallName = usesActiveCallName();

  const user = useUserDetails();
  const callId = useCallId();
  const isCallModalOpen = useIsCallModalOpen();

  const [duration, setDuration] = useState(0);
  const [resetTimer, setResetTimer] = useState(false);

  let userAgent;
  let registerer;
  const remoteStream = new MediaStream();

  const connectSIP = async () => {
    try {
      const sipUri = UserAgent.makeURI(
        `sip:${user.username}@${user.domain}:${process.env.REACT_APP_SIP_PORT}`
      );
      if (!sipUri) {
        console.error('Invalid SIP URI');
        return;
      }

      const userAgentOptions = {
        uri: sipUri,
        authorizationPassword: user?.password,
        authorizationUsername: user?.username,
        transportOptions: {
          server: config.WSS_URL
        }
      };
      dispatch(setSipStatus('Connecting'));
      console.log('Creating UserAgent...');
      userAgent = new UserAgent(userAgentOptions);

      console.log('Starting UserAgent...');
      await userAgent.start();

      console.log('Registering UserAgent...');
      const registerer = new Registerer(userAgent);
      await registerer.register();

      console.log('UserAgent registered and ready for calls.');
      dispatch(setSipStatus('Register'));
      userAgent.delegate = {
        onInvite: async (invitation) => {
          console.log('Incoming call...');
          try {
            await invitation.accept({
              sessionDescriptionHandlerOptions: {
                constraints: {
                  audio: true,
                  video: false
                }
              }
            });
            setupRemoteMedia(invitation);
            console.log('Call answered!');
            dispatch(setCallModalOpen(true));
          } catch (err) {
            console.error('Error answering call:', err);
          }
        }
      };
    } catch (error) {
      console.error('SIP Connection Error:', error);
    }
  };

  const registerUA = async () => {
    try {
      registerer = new Registerer(userAgent);
      registerer
        .register()
        .then(async () => {
          dispatch(setSipStatus('Register'));
          console.log('UA Successfully registered ...');
        })
        .catch(async (error) => {
          console.error('Failed to send REGISTER ==> ', error);
        });
    } catch (error) {
      console.error('CATCH FOUND -  registerUA ==> ', error);
    }
  };

  const reconnectUA = async () => {
    try {
      userAgent
        .reconnect()
        .then(async () => {
          dispatch(setSipStatus('Connecting'));
          await registerUA();
        })
        .catch(async (error) => {
          console.error('UA Reconnection error ==> ', error);
        });
    } catch (error) {
      console.error('CATCH FOUND -  reconnectUA ==> ', error);
    }
  };

  const setupRemoteMedia = (mediaSession) => {
    const mediaElement = document.getElementById('mediaElement');
    try {
      mediaSession.sessionDescriptionHandler.peerConnection.getReceivers().forEach((receiver) => {
        if (receiver.track) {
          remoteStream.addTrack(receiver.track);
          mediaElement.srcObject = remoteStream;
          mediaElement.play();
        }
      });
    } catch (error) {
      console.log('Media audio session error - ', error);
    }
  };

  useEffect(() => {
    const handleOnline = async () => {
      reconnectUA();
    };

    const handleOffline = () => {
      dispatch(setSipStatus('Un-Register'));
      alert('Please check your connection');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    Object.defineProperty(window, 'isSecureContext', { get: () => true });

    if (!userAgent) {
      connectSIP();
    } else {
      reconnectUA();
    }

    return () => {
      dispatch(setSipStatus('Un-Register'));
      console.log('Stopping UserAgent...');
      userAgent.stop();
    };
  }, []);

  const onHangUpCall = async () => {
    try {
      const payload = {
        call_uuid: callId
      };
      const response = await dispatch(hangupCall(payload)).unwrap();
      if (response && response.success) {
        dispatch(setCallModalOpen(false));
        dispatch(setCallId(null));
        dispatch(setActiveCallName(null));
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const handleReset = () => {
    setResetTimer(true);
    setTimeout(() => setResetTimer(true), 0);
  };

  useEffect(() => {
    handleReset();
  }, [isCallModalOpen]);

  if (!isCallModalOpen) {
    return;
  }

  return (
    <div className="supervisor-modal shadow-sm">
      <div className="text-center text-white idle-time d-flex align-items-center gap-2">
        <div className="bg-green pulse"></div>
        <span className="text-uppercase">{activeCallName}</span>
        <span>
          <AdminTimer
            duration={duration}
            setDuration={setDuration}
            resetTimer={resetTimer}
            setResetTimer={setResetTimer}
          />
        </span>
      </div>
      <div className="text-center call-action-btn-container">
        <Button
          color="danger"
          className="call-end-btn"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onHangUpCall();
          }}>
          <i className="bi bi-telephone-x-fill" />
        </Button>
      </div>
    </div>
  );
};

export default CallingModal;
